import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  activeCustomerApi,
  banCustomerApi,
  createCustomerApi,
  deleteCustomerApi,
  getAllCustomersApi,
  getCustomerOrderHistoryApi,
  getCustomerPointsHistoryApi,
  getSingleCustomerApi,
  searchCustomersApi,
  toggleCustomerPointBaseApi,
  unbanAddressApi,
  updateCustomerApi,
} from "@/api/new-api/customer/customer";

export const fetchCustomers = createAsyncThunk(
  "customer/fetchCustomers",
  async (
    { restaurantId, page = 1, per_page = 10, search_term = "", status = "" } = {},
    { rejectWithValue },
  ) => {
    const request = search_term.trim()
      ? searchCustomersApi(restaurantId, { search_term, page, per_page, status })
      : getAllCustomersApi(restaurantId, { page, per_page, status });

    const res = await request;

    if (res.status === "success") {
      return res.data;
    }

    return rejectWithValue(res.message);
  },
);

export const toggleCustomerPointBase = createAsyncThunk(
  "customer/toggleCustomerPointBase",
  async (customerId, { rejectWithValue }) => {
    const res = await toggleCustomerPointBaseApi(customerId);

    if (res.status === "success") {
      return res;
    }

    return rejectWithValue(res.message);
  },
);

export const removeCustomerById = createAsyncThunk(
  "customer/removeCustomerById",
  async (customerId, { rejectWithValue }) => {
    const res = await deleteCustomerApi(customerId);

    if (res.status === "success") {
      return customerId;
    }

    return rejectWithValue(res.message);
  },
);

export const banCustomerById = createAsyncThunk(
  "customer/banCustomerById",
  async ({ customerId, payload }, { rejectWithValue }) => {
    const res = await banCustomerApi(customerId, payload);

    if (res.status === "success") {
      return res;
    }

    return rejectWithValue(res.message);
  },
);

export const activeCustomerById = createAsyncThunk(
  "customer/activeCustomerById",
  async ({ customerId, payload }, { rejectWithValue }) => {
    const res = await activeCustomerApi(customerId, payload);

    if (res.status === "success") {
      return res;
    }

    return rejectWithValue(res.message);
  },
);

export const unbanAddressById = createAsyncThunk(
  "customer/unbanAddressById",
  async ({ addressId, customerId }, { rejectWithValue }) => {
    const res = await unbanAddressApi(addressId);

    if (res.status === "success") {
      return { ...res, customerId }; // Pass customerId along to easily update state
    }

    return rejectWithValue(res.message);
  },
);

export const createCustomer = createAsyncThunk(
  "customer/createCustomer",
  async (payload, { rejectWithValue }) => {
    const res = await createCustomerApi(payload);

    if (res.status === "success") {
      return res;
    }

    return rejectWithValue(res.message);
  },
);

export const updateCustomer = createAsyncThunk(
  "customer/updateCustomer",
  async ({ customerId, payload }, { rejectWithValue }) => {
    const res = await updateCustomerApi(customerId, payload);

    if (res.status === "success") {
      return res;
    }

    return rejectWithValue(res.message);
  },
);

export const fetchCustomerDetails = createAsyncThunk(
  "customer/fetchCustomerDetails",
  async (customerId, { rejectWithValue }) => {
    const res = await getSingleCustomerApi(customerId);

    if (res.status === "success") {
      return res.data;
    }

    return rejectWithValue(res.message);
  },
);

export const fetchCustomerOrderHistory = createAsyncThunk(
  "customer/fetchCustomerOrderHistory",
  async (
    {
      restaurantId,
      customerId,
      page = 1,
      per_page = 10,
      search_term = "",
      order_type = "",
      sort_field = "order_id",
      sort_order = "desc",
    } = {},
    { rejectWithValue },
  ) => {
    const res = await getCustomerOrderHistoryApi(restaurantId, customerId, {
      page,
      per_page,
      search_term,
      order_type,
      sort_field,
      sort_order,
    });

    if (res.status === "success") {
      return res.data;
    }

    return rejectWithValue(res.message);
  },
);

export const fetchCustomerPointsHistory = createAsyncThunk(
  "customer/fetchCustomerPointsHistory",
  async (
    {
      restaurantId,
      customerId,
      page = 1,
      per_page = 10,
      search_term = "",
      order_type = "",
      sort_field = "created_at",
      sort_order = "desc",
    } = {},
    { rejectWithValue },
  ) => {
    const res = await getCustomerPointsHistoryApi(restaurantId, customerId, {
      page,
      per_page,
      search_term,
      order_type,
      sort_field,
      sort_order,
    });

    if (res.status === "success") {
      return res.data;
    }

    return rejectWithValue(res.message);
  },
);

const upsertCustomerInList = (list, customer) => {
  if (!customer?.id) return list;

  const existingIndex = list.findIndex((item) => item.id === customer.id);

  if (existingIndex === -1) {
    return [customer, ...list];
  }

  return list.map((item) => (item.id === customer.id ? customer : item));
};

const updateBannedStatusInList = (list, customerId, isBanned) =>
  list.map((customer) =>
    customer.id === customerId ? { ...customer, is_banned: isBanned } : customer,
  );

const initialState = {
  stats: null,
  customers: [],
  pagination: null,
  customerDetails: null,
  customerDetailsLoading: false,
  orderHistoryStats: null,
  orderHistories: [],
  orderHistoryPagination: null,
  orderHistoryLoading: false,
  pointsHistoryStats: null,
  pointsHistories: [],
  pointsHistoryPagination: null,
  pointsHistoryLoading: false,
  getLoading: false,
  updateLoading: false,
  statusUpdateLoading: false,
  formLoading: false,
  deleteLoading: false,
  error: null,
};

const customerSlice = createSlice({
  name: "customer",
  initialState,
  reducers: {
    clearCustomerError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCustomers.pending, (state) => {
        state.getLoading = true;
        state.error = null;
      })
      .addCase(fetchCustomers.fulfilled, (state, action) => {
        state.getLoading = false;
        state.stats = action.payload?.stats || null;
        state.customers = action.payload?.customers || [];
        state.pagination = action.payload?.pagination || null;
      })
      .addCase(fetchCustomers.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
      })
      .addCase(toggleCustomerPointBase.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
      })
      .addCase(toggleCustomerPointBase.fulfilled, (state, action) => {
        state.updateLoading = false;

        const updatedCustomer =
          action.payload?.data?.customer || action.payload?.customer || null;

        if (updatedCustomer?.id !== undefined) {
          state.customers = upsertCustomerInList(state.customers, updatedCustomer);

          if (state.customerDetails?.id === updatedCustomer.id) {
            state.customerDetails = updatedCustomer;
          }
        }
      })
      .addCase(toggleCustomerPointBase.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload;
      })
      .addCase(banCustomerById.pending, (state) => {
        state.statusUpdateLoading = true;
        state.error = null;
      })
      .addCase(banCustomerById.fulfilled, (state, action) => {
        state.statusUpdateLoading = false;

        const customerId = action.payload?.data?.customer_id;
        const isBanned = action.payload?.data?.is_banned;

        if (customerId !== undefined && isBanned !== undefined) {
          const previousCustomer = state.customers.find(
            (customer) => customer.id === customerId,
          );

          state.customers = updateBannedStatusInList(
            state.customers,
            customerId,
            isBanned,
          );

          if (state.customerDetails?.id === customerId) {
            state.customerDetails = {
              ...state.customerDetails,
              is_banned: isBanned,
            };
          }

          if (
            state.stats &&
            previousCustomer?.is_banned !== undefined &&
            previousCustomer.is_banned !== isBanned
          ) {
            if (isBanned) {
              state.stats.total_banned_customers =
                (state.stats.total_banned_customers || 0) + 1;
            } else {
              state.stats.total_banned_customers = Math.max(
                (state.stats.total_banned_customers || 0) - 1,
                0,
              );
            }
          }
        }
      })
      .addCase(banCustomerById.rejected, (state, action) => {
        state.statusUpdateLoading = false;
        state.error = action.payload;
      })
      .addCase(activeCustomerById.pending, (state) => {
        state.statusUpdateLoading = true;
        state.error = null;
      })
      .addCase(activeCustomerById.fulfilled, (state, action) => {
        state.statusUpdateLoading = false;

        const customerId = action.payload?.data?.customer_id;
        const isBanned = action.payload?.data?.is_banned;
        const addressUnbanned = action.meta?.arg?.payload?.unban_address;

        if (customerId !== undefined && isBanned !== undefined) {
          const previousCustomer = state.customers.find(
            (customer) => customer.id === customerId,
          );

          state.customers = state.customers.map((customer) => {
            if (customer.id === customerId) {
              const updatedAddresses = addressUnbanned && customer.addresses
                ? customer.addresses.map((addr) =>
                    addr.id === customer.active_address?.id ? { ...addr, is_banned: false } : addr
                  )
                : customer.addresses;
              const updatedActiveAddress = addressUnbanned && customer.active_address
                ? { ...customer.active_address, is_banned: false }
                : customer.active_address;
              return {
                ...customer,
                is_banned: isBanned,
                addresses: updatedAddresses,
                active_address: updatedActiveAddress,
              };
            }
            return customer;
          });

          if (state.customerDetails?.id === customerId) {
            const updatedAddresses = addressUnbanned && state.customerDetails.addresses
              ? state.customerDetails.addresses.map((addr) =>
                  addr.id === state.customerDetails.active_address?.id ? { ...addr, is_banned: false } : addr
                )
              : state.customerDetails.addresses;
            const updatedActiveAddress = addressUnbanned && state.customerDetails.active_address
              ? { ...state.customerDetails.active_address, is_banned: false }
              : state.customerDetails.active_address;
            
            state.customerDetails = {
              ...state.customerDetails,
              is_banned: isBanned,
              addresses: updatedAddresses,
              active_address: updatedActiveAddress,
            };
          }

          if (
            state.stats &&
            previousCustomer?.is_banned !== undefined &&
            previousCustomer.is_banned !== isBanned
          ) {
            if (isBanned) {
              state.stats.total_banned_customers =
                (state.stats.total_banned_customers || 0) + 1;
            } else {
              state.stats.total_banned_customers = Math.max(
                (state.stats.total_banned_customers || 0) - 1,
                0,
              );
            }
          }
        }
      })
      .addCase(activeCustomerById.rejected, (state, action) => {
        state.statusUpdateLoading = false;
        state.error = action.payload;
      })
      .addCase(unbanAddressById.pending, (state) => {
        state.statusUpdateLoading = true;
        state.error = null;
      })
      .addCase(unbanAddressById.fulfilled, (state, action) => {
        state.statusUpdateLoading = false;
        const customerId = action.payload?.customerId;
        const addressId = action.payload?.data?.address_id || action.payload?.address_id;
        
        if (customerId !== undefined && addressId !== undefined) {
          state.customers = state.customers.map((customer) => {
            if (customer.id === customerId) {
              const updatedAddresses = (customer.addresses || []).map((addr) =>
                addr.id === addressId ? { ...addr, is_banned: false } : addr
              );
              const updatedActiveAddress = customer.active_address?.id === addressId
                ? { ...customer.active_address, is_banned: false }
                : customer.active_address;
              return {
                ...customer,
                addresses: updatedAddresses,
                active_address: updatedActiveAddress,
              };
            }
            return customer;
          });

          if (state.customerDetails?.id === customerId) {
            const updatedAddresses = (state.customerDetails.addresses || []).map((addr) =>
              addr.id === addressId ? { ...addr, is_banned: false } : addr
            );
            const updatedActiveAddress = state.customerDetails.active_address?.id === addressId
              ? { ...state.customerDetails.active_address, is_banned: false }
              : state.customerDetails.active_address;
            
            state.customerDetails = {
              ...state.customerDetails,
              addresses: updatedAddresses,
              active_address: updatedActiveAddress,
            };
          }
        }
      })
      .addCase(unbanAddressById.rejected, (state, action) => {
        state.statusUpdateLoading = false;
        state.error = action.payload;
      })
      .addCase(createCustomer.pending, (state) => {
        state.formLoading = true;
        state.error = null;
      })
      .addCase(createCustomer.fulfilled, (state, action) => {
        state.formLoading = false;

        const createdCustomer =
          action.payload?.data?.customer || action.payload?.customer || null;

        if (createdCustomer?.id !== undefined) {
          state.customers = [createdCustomer, ...state.customers];
        }

        if (state.stats) {
          state.stats.total_customers = (state.stats.total_customers || 0) + 1;
          if (createdCustomer?.is_banned) {
            state.stats.total_banned_customers =
              (state.stats.total_banned_customers || 0) + 1;
          }
        }
      })
      .addCase(createCustomer.rejected, (state, action) => {
        state.formLoading = false;
        state.error = action.payload;
      })
      .addCase(updateCustomer.pending, (state) => {
        state.formLoading = true;
        state.error = null;
      })
      .addCase(updateCustomer.fulfilled, (state, action) => {
        state.formLoading = false;

        const updatedCustomer =
          action.payload?.data?.customer || action.payload?.customer || null;

        if (updatedCustomer?.id !== undefined) {
          state.customers = upsertCustomerInList(state.customers, updatedCustomer);

          if (state.customerDetails?.id === updatedCustomer.id) {
            state.customerDetails = updatedCustomer;
          }
        }
      })
      .addCase(updateCustomer.rejected, (state, action) => {
        state.formLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchCustomerDetails.pending, (state) => {
        state.customerDetailsLoading = true;
        state.error = null;
      })
      .addCase(fetchCustomerDetails.fulfilled, (state, action) => {
        state.customerDetailsLoading = false;

        const fetchedCustomer =
          action.payload?.customer || action.payload?.data?.customer || null;

        state.customerDetails = fetchedCustomer;

        if (fetchedCustomer?.id !== undefined) {
          state.customers = upsertCustomerInList(state.customers, fetchedCustomer);
        }
      })
      .addCase(fetchCustomerDetails.rejected, (state, action) => {
        state.customerDetailsLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchCustomerOrderHistory.pending, (state) => {
        state.orderHistoryLoading = true;
        state.error = null;
      })
      .addCase(fetchCustomerOrderHistory.fulfilled, (state, action) => {
        state.orderHistoryLoading = false;
        state.orderHistoryStats = action.payload?.stats || null;
        state.orderHistories = action.payload?.orders || [];
        state.orderHistoryPagination = action.payload?.pagination || null;
      })
      .addCase(fetchCustomerOrderHistory.rejected, (state, action) => {
        state.orderHistoryLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchCustomerPointsHistory.pending, (state) => {
        state.pointsHistoryLoading = true;
        state.error = null;
      })
      .addCase(fetchCustomerPointsHistory.fulfilled, (state, action) => {
        state.pointsHistoryLoading = false;
        state.pointsHistoryStats = action.payload?.stats || null;
        state.pointsHistories = action.payload?.reward_point_histories || [];
        state.pointsHistoryPagination = action.payload?.pagination || null;
      })
      .addCase(fetchCustomerPointsHistory.rejected, (state, action) => {
        state.pointsHistoryLoading = false;
        state.error = action.payload;
      })
      .addCase(removeCustomerById.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
      })
      .addCase(removeCustomerById.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.customers = state.customers.filter(
          (customer) => customer.id !== action.payload,
        );
      })
      .addCase(removeCustomerById.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearCustomerError } = customerSlice.actions;
export default customerSlice.reducer;